/**
 * Single source of truth for the default set of event types an org gets at
 * bootstrap, plus the canonical conversation row, plus the factory every
 * other piece of code (handlers, mappers, tests) uses to *create* an event
 * type without spelling out its fields.
 *
 * The slug literals `CHECK_UP`, `FOLLOW_UP`, `CONVERSATION` and their
 * default colors / durations appear ONCE — in `DEFAULT_EVENT_TYPE_SEEDS`
 * below. No other file should mention them in code. Tests use
 * `buildEventType()` to fabricate event types with auto-generated slugs;
 * the bootstrap and the org-creation seed handler iterate
 * `DEFAULT_EVENT_TYPE_SEEDS` and call `buildEventTypeFromSeed()`.
 *
 * Authoring path for adding a new default type:
 *   1. Add a row to `DEFAULT_EVENT_TYPE_SEEDS`.
 *   2. Add the matching INSERT to `prisma/migrations/<date>_backfill_default_event_types`
 *      (one literal SQL row per seed; runtime parity is enforced by the
 *      seed handler at org creation).
 *   3. Re-run `bun typecheck` and `bun test`.
 */

import type { EventTypeDefinition } from "./event-type-definition";

export type EventTypeKind = "scheduled" | "conversation";

export interface DefaultEventTypeSeed {
	readonly colorBackground: string;
	readonly colorRing: string;
	readonly colorText: string;
	readonly durationMinutes: number;
	/** `conversation` for the system-managed singleton; `scheduled` for everything else. */
	readonly kind: EventTypeKind;
	/** User-facing display label. */
	readonly name: string;
	/** UPPER_SNAKE_CASE slug — stable id used by the API + AI tools. */
	readonly slug: string;
}

/**
 * The complete default seed. Add / remove rows here to change what an org
 * starts with — colors, durations, kind included.
 *
 * Order matters for `scheduled` rows — it becomes the initial UI ordering
 * (lower index → earlier in the editor list). The conversation row is
 * always pinned to the bottom by the editor regardless of position here.
 */
export const DEFAULT_EVENT_TYPE_SEEDS: readonly DefaultEventTypeSeed[] = [
	{
		slug: "CHECK_UP",
		name: "Check-up",
		kind: "scheduled",
		durationMinutes: 30,
		colorBackground: "#e2eee9",
		colorRing: "#12b981",
		colorText: "#065f46",
	},
	{
		slug: "FOLLOW_UP",
		name: "Follow-up",
		kind: "scheduled",
		durationMinutes: 45,
		colorBackground: "#e2e8f4",
		colorRing: "#3c82f6",
		colorText: "#1e40af",
	},
	{
		slug: "CONVERSATION",
		name: "Conversation",
		kind: "conversation",
		durationMinutes: 30,
		colorBackground: "#edae49",
		colorRing: "#edae49",
		colorText: "#ffffff",
	},
] as const;

/**
 * Slug of the singleton conversation event type. Exposed as a named
 * constant only because the migration / SQL seed needs a literal — runtime
 * code should branch on `EventType.kind === 'conversation'` instead.
 */
export const CONVERSATION_EVENT_TYPE_SLUG = "CONVERSATION";

/** The conversation seed (kind === 'conversation'). */
export const CONVERSATION_EVENT_TYPE_SEED: DefaultEventTypeSeed = (() => {
	const seed = DEFAULT_EVENT_TYPE_SEEDS.find((s) => s.kind === "conversation");
	if (!seed) {
		throw new Error(
			"[event-type-seed] DEFAULT_EVENT_TYPE_SEEDS is missing the conversation row"
		);
	}
	return seed;
})();

/**
 * Look up a default seed by slug (case-insensitive). Returns `undefined`
 * for an unknown slug — callers handle their own fallback.
 */
export function findEventTypeSeed(
	slug: string | null | undefined
): DefaultEventTypeSeed | undefined {
	if (!slug) {
		return undefined;
	}
	const upper = slug.toUpperCase();
	return DEFAULT_EVENT_TYPE_SEEDS.find((s) => s.slug.toUpperCase() === upper);
}

// ============================================================================
// Factory — the one place that knows how to *create* an EventType row.
// ============================================================================

/**
 * Per-slot ceiling a brand-new event type starts at, for both roles (AI agent
 * and secretary / UI). `null` (unlimited) is still a valid value a user can
 * pick by clearing the field — this is only the starting point. Mirrored by
 * `DEFAULT_MAX_PER_SLOT` in the server domain and by the
 * `core.event_type."maxPerSlot*"` column defaults.
 */
export const DEFAULT_MAX_PER_SLOT = 3;

/**
 * Default values used when the caller hasn't supplied an override.
 * Centralised so a new field on `EventTypeDefinition` only needs a default
 * here — every consumer of the factory inherits it automatically.
 */
const EVENT_TYPE_DEFAULTS: Omit<EventTypeDefinition, "id" | "name"> = {
	durationMinutes: 30,
	maxPerSlotAgent: DEFAULT_MAX_PER_SLOT,
	maxPerSlotSecretary: DEFAULT_MAX_PER_SLOT,
	allowMultipleFutureEvents: false,
	allowMultipleEventsPerDay: false,
	maxFutureEventsPerType: null,
	bookingHorizonAgentDays: null,
	bookingHorizonSecretaryDays: null,
	minimumBookingNotice: 0,
	beforeBufferMinutes: 0,
	afterBufferMinutes: 0,
	requiresApproval: false,
	approvalTtlHours: null,
	description: null,
	maxSpanDays: null,
	resourceName: null,
};

/** Auto-incrementing counter used to mint anonymous slugs in `buildEventType()`. */
let _factoryCounter = 0;

/**
 * Reset the factory's anonymous-slug counter. Call this from a test
 * `beforeEach` hook when slug stability across runs matters; otherwise
 * the counter keeps incrementing for the process lifetime, which is fine
 * for production seed code (it's only ever called per-org once).
 */
export function resetEventTypeFactory(): void {
	_factoryCounter = 0;
}

/**
 * Build a complete `EventTypeDefinition` from optional overrides.
 *
 * Use this everywhere a new event type needs to exist:
 *   - bootstrap defaults (via `buildEventTypeFromSeed` below)
 *   - org-creation seed handler
 *   - tests that need an event type to exercise behaviour against
 *
 * The slug is auto-generated when not supplied — tests don't need to invent
 * names like `'TEST_TYPE'` or `'foo'`. Callers override the field they
 * care about and the rest stays at safe defaults.
 */
export function buildEventType(
	overrides: Partial<EventTypeDefinition> = {}
): EventTypeDefinition {
	const n = ++_factoryCounter;
	const id = overrides.id ?? `EVENT_TYPE_${n}`;
	const name = overrides.name ?? `Event Type ${n}`;
	return {
		...EVENT_TYPE_DEFAULTS,
		...overrides,
		id,
		name,
	};
}

/**
 * Build an `EventTypeDefinition` from a default seed entry. Every
 * non-default field on the seed (`durationMinutes`) is mirrored onto the
 * record; capacity / future-events flags stay at their factory defaults.
 */
export function buildEventTypeFromSeed(
	seed: DefaultEventTypeSeed
): EventTypeDefinition {
	return buildEventType({
		id: seed.slug,
		name: seed.name,
		durationMinutes: seed.durationMinutes,
	});
}

/**
 * Build the runtime cache projected onto `AppConfig.eventTypes[]`. Only
 * scheduled seeds are emitted — the conversation row lives on the EventType
 * table but is intentionally omitted from this list (the calendar legend
 * uses `kind === 'conversation'` to render it separately).
 */
export function buildDefaultEventTypeRuntimeCache(): EventTypeDefinition[] {
	return DEFAULT_EVENT_TYPE_SEEDS.filter((s) => s.kind === "scheduled").map(
		buildEventTypeFromSeed
	);
}
